import React, {ForwardedRef, useCallback, useMemo, useState} from "react";
import {Box, InputLabel, List, ListItem, TextField, Typography, useTheme} from "@mui/material";
import {TextFieldProps} from "@mui/material/TextField/TextField";
import {PasswordVisibilityToggle} from "./passwordVisibility.toggle";
import {RegexConstants} from "../../common/constants/regex.constants";
import {useDebounce} from "../../common/hooks/useDebounce";
import CheckedCircle from "../../common/icons/misc/checkedCircle";

type PasswordInputProps = TextFieldProps & {
    hint?: boolean;
    errorMessage?: string;
    fullHeight?: boolean;
    readOnly?: boolean;
    optional?: boolean;
};

export const PasswordInput = React.forwardRef((props: PasswordInputProps, ref: ForwardedRef<PasswordInputProps>) => {

    const {
        label,
        id,
        focused,
        error,
        onFocus,
        onBlur,
        onChange,
        errorMessage,
        hint,
        readOnly,
        optional,
        InputProps,
        className,
        ...rest
    } = props;

    const theme = useTheme();
    const [isFocused, setIsFocused] = useState(readOnly ? false : focused);
    const [showPassword, setShowPassword] = useState<boolean>(false);
    const [contains8Chars, setContains8Chars] = useState<boolean>(false);
    const [containsUpperCase, setContainsUpperCase] = useState<boolean>(false);
    const [containsSymbol, setContainsSymbol] = useState<boolean>(false);
    const [containsNumber, setContainsNumber] = useState<boolean>(false);

    const togglePasswordVisibility = useCallback(() => {
        setShowPassword(!showPassword);
    }, [showPassword])

    const handleChange = useDebounce((ev: React.ChangeEvent<HTMLInputElement>) => {
        const value = ev.target.value;

        setContains8Chars(RegexConstants.HAS_MINIMUM_8_CHARS.test(value));
        setContainsUpperCase(RegexConstants.PASSWORD_HAS_UPPERCHAR.test(value));
        setContainsSymbol(RegexConstants.PASSWORD_HAS_SYMBOL.test(value));
        setContainsNumber(RegexConstants.PASSWORD_HAS_NUMBER.test(value));

        onChange?.(ev);
    }, 100)

    const InternalInputProps = useMemo(
        () => ({
            readOnly: readOnly,
            endAdornment: <PasswordVisibilityToggle onClick={togglePasswordVisibility} showText={showPassword}/>,
            ...InputProps,
        }),
        [readOnly, InputProps, showPassword]
    );

    return (
        <Box className={className}>
            {label && (
                <InputLabel error={error} focused={isFocused} className={"input-label"} htmlFor={id} sx={{whiteSpace: "pre-wrap", mb: 0.5}}>
                    {label}
                </InputLabel>
            )}
            <TextField
                inputRef={ref}
                className={"input-field"}
                margin="none"
                error={error}
                required={false}
                fullWidth
                focused={focused}
                onFocus={(ev) => {
                    setIsFocused(!readOnly);
                    onFocus?.(ev);
                }}
                onBlur={(ev) => {
                    setIsFocused(false);
                    onBlur?.(ev);
                }}
                id={id}
                onChange={handleChange}
                type={showPassword ? "text" : "password"}
                InputProps={InternalInputProps}
                {...rest}
            />
            {props.errorMessage && (
                <Typography component="div" className="error-message" variant={"body2"}>
                    {errorMessage}
                </Typography>
            )}
            {props.hint && (
                <>
                    <Typography component="div" className={"input-help"} mt={0.5} variant={"body2"}>
                        Your password must contain
                    </Typography>
                    <List sx={{listStyleType: 'disc', paddingX: 2.5, '& .MuiListItem-root': {display: 'list-item'}}} className={"input-help"}>
                        <ListItem disableGutters disablePadding>
                            <Typography variant={"body2"} sx={{color: contains8Chars ? theme.palette.thatsMe.textSuccess : 'inherit'}}>
                                minimum 8 characters {contains8Chars && (
                                <CheckedCircle style={{verticalAlign: "middle"}}/>)}
                            </Typography>
                        </ListItem>
                        <ListItem disableGutters disablePadding>
                            <Typography variant={"body2"} sx={{color: containsUpperCase ? theme.palette.thatsMe.textSuccess : 'inherit'}}>
                                an uppercase letter {containsUpperCase && (
                                <CheckedCircle style={{verticalAlign: "middle"}}/>)
                            }
                            </Typography>
                        </ListItem>
                        <ListItem disableGutters disablePadding>
                            <Typography variant={"body2"} sx={{color: containsSymbol ? theme.palette.thatsMe.textSuccess : 'inherit'}}>
                                a symbol {containsSymbol && (
                                <CheckedCircle style={{verticalAlign: "middle"}}/>)
                            }
                            </Typography>
                        </ListItem>
                        <ListItem disableGutters disablePadding>
                            <Typography variant={"body2"} sx={{color: containsNumber ? theme.palette.thatsMe.textSuccess : 'inherit'}}>
                                a number {containsNumber && (
                                <CheckedCircle style={{verticalAlign: "middle"}}/>)
                            }
                            </Typography>
                        </ListItem>
                    </List>
                </>
            )}
        </Box>
    );
});

